import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { apiBaseUrl } from '../../config';
import { useInView } from "react-intersection-observer";
import axios from 'axios';
import parse from "html-react-parser";

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

const AboutPage = () => {
  const isCarouselInitialized = useRef(false);
  const isDoctorCarouselInitialized = useRef(false);
  const [isCarouselReady, setIsCarouselReady] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState("collapseOne");
  const [cms, setCms] = useState([]);
  
  // Dynamic data states
  const [principles, setPrinciples] = useState([]);
  const [timelineItems, setTimelineItems] = useState([]);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Fetch CMS
  const fetchCms = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/all-get-cms`);
  
      if (data.status) {
        const cmsData = {};
        data.cms_pages.forEach((item) => {
          cmsData[item.cms_id] = item;
        });
        setCms(cmsData);
      } else {
        setCms({});
      }
    } catch (err) {
      console.error("Error fetching CMS pages:", err);
      setCms({});
    }
  };

  // Fetch all items from database
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/items`, {
        params: { limit: 1000 }
      });
      
      if (response.data.status) {
        const allItems = response.data.data;
        setPrinciples(allItems.filter(item => item.type === 'about_principle'));
        setTimelineItems(allItems.filter(item => item.type === 'about_timeline'));
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchCms();
    fetchItems();
  }, []);

  // Load carousel
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jQueryModule = await import("jquery");
        const $ = jQueryModule.default || jQueryModule;
        window.jQuery = $;
        window.$ = $;
        await import("owl.carousel");
        setIsCarouselReady(true);
        setTimeout(() => {
          initializeCarousels();
        }, 500);
      } catch (error) {
        console.error("Error loading carousel:", error);
        setIsCarouselReady(false);
      }
    };

    const initializeCarousels = () => {
      const $ = window.$;
      if (!$ || !$.fn || !$.fn.owlCarousel) {
        console.error("jQuery or owlCarousel not available");
        return;
      }

      if (!isCarouselInitialized.current) {
        const testimonialElement = $(".testimonial-slider");
        if (testimonialElement.length) {
          try {
            testimonialElement.owlCarousel({
              items: 1,
              loop: true,
              autoplay: true,
              autoplayTimeout: 4000,
              smartSpeed: 800,
              dots: true,
              nav: false,
              margin: 30,
            });
            isCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing testimonial carousel:", e);
          }
        }
      }

      if (!isDoctorCarouselInitialized.current) {
        const doctorElement = $(".doctor-slider");
        if (doctorElement.length) {
          try {
            doctorElement.owlCarousel({
              loop: true,
              margin: 25,
              nav: false,
              dots: false,
              autoplay: true,
              autoplayTimeout: 3000,
              smartSpeed: 800,
              responsive: {
                0: { items: 1 },
                576: { items: 2 },
                992: { items: 3 },
                1200: { items: 4 },
              },
            });
            isDoctorCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing doctor carousel:", e);
          }
        }
      }
    };

    loadCarousel();

    return () => {
      const $ = window.$;
      if ($ && $.fn && $.fn.owlCarousel) {
        try {
          if (isCarouselInitialized.current) {
            $(".testimonial-slider").owlCarousel("destroy");
            isCarouselInitialized.current = false;
          }
          if (isDoctorCarouselInitialized.current) {
            $(".doctor-slider").owlCarousel("destroy");
            isDoctorCarouselInitialized.current = false;
          }
        } catch (e) {
          console.log("Carousel already destroyed or not initialized");
        }
      }
    };
  }, []);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  return (
    <div>
      <Helmet>
        <title>About us</title>
      </Helmet>

      {/* ============================================ */}
      {/* HERO SECTION - CMS ID 81 */}
      {/* ============================================ */}
      <section className="hero-section" style={{ padding: "40px 0px" }}>
        <div className="container text-center">
          <h2 className="predyx-main-title mb-2" style={{ lineHeight: "35px" }}>
            {parse(cms[81]?.cms_page_heading || "ABOUT <span>PREDYX</span>")}
          </h2>
          <h4 style={{ fontSize: "18px", marginTop: "10px", fontWeight: "700", marginBottom: "15px" }}>
            {cms[81]?.cms_page_small_description || "We Didn't Set Out to Build Another Healthcare Platform. We Set Out to Change How Healthcare Thinks."}
          </h4>
          {parse(cms[81]?.cms_page_description || "")}
          <img
            src={`${apiBaseUrl}/assets/images/${cms[81]?.cms_image}`}
            alt="About us"
            className="img-fluid"
            style={{ marginTop: "20px", borderRadius: "10px" }}
          />
        </div>
      </section>

      {/* ============================================ */}
      {/* WHY PREDYX EXISTS - CMS ID 82 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4 pr-5 pl-6">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[82]?.cms_page_heading || "Why <span>predyx Exists</span>")}
            </h2>
          </div>
          <div className="predyx-content-box">
            <div className="info-card" style={{ padding: "0px" }}>
              {parse(cms[82]?.cms_page_description || "")}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOUNDATION & BUILT TO INTEGRATE - CMS ID 83, 84 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section pt-2">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 col-md-6">
              <div className="predyx-header text-center mb-4 pr-5 pl-6">
                <h2 className="predyx-main-title mt-2">
                  {parse(cms[83]?.cms_page_heading || "The Foundation of <br/><span>Clinical Intelligence</span>")}
                </h2>
              </div>
              <div className="predyx-content-box">
                <div className="info-card" style={{ padding: "0px" }}>
                  {parse(cms[83]?.cms_page_description || "")}
                </div>
              </div>
            </div>

            <div className="col-lg-6 col-md-6">
              <div className="predyx-header text-center mb-4">
                <h2 className="predyx-main-title mt-2 mb-2">
                  {parse(cms[84]?.cms_page_heading || "Built to Integrate. <br/><span>Never to Replace.</span>")}
                </h2>
              </div>
              <div className="predyx-content-box">
                {parse(cms[84]?.cms_page_description || "")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* ORIGIN & PHILOSOPHY - CMS ID 85, 86 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section pt-2">
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2 mb-2">
              {parse(cms[85]?.cms_page_heading || "Our Origin & <span>Philosophy</span>")}
            </h2>
          </div>
          <div className="predyx-content-box">
            {parse(cms[85]?.cms_page_description || "")}
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="quote-box">
                <h4>{cms[86]?.cms_page_heading || "Predyx is not simply another software platform or diagnostic tool. It is a new architecture for clinical reasoning."}</h4>
                <p>{cms[86]?.cms_page_small_description || "This philosophy continues to guide every algorithm, interface, and integration we build."}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* GUIDING PRINCIPLES - CMS ID 87 + Items */}
      {/* ============================================ */}
      <section className="explain-section predyx-section pt-2">
        <div className="container">
          <div className="predyx-header text-center mb-4 pr-5 pl-6">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[87]?.cms_page_heading || "Our Guiding <span>Principles</span>")}
            </h2>
          </div>
          <div className="mns row">
            {principles.map((item, index) => (
              <div className="col-lg-6 col-md-6" key={index}>
                <div className="predyx-content-box">
                  <div className="info-card" style={{ padding: "0px" }}>
                    <h5>{item.title}</h5>
                    <p>{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* EVOLUTION OF CLINICAL UNDERSTANDING - CMS ID 89 + Items */}
      {/* ============================================ */}
      <section className="predyx7-section py-5" style={{ background: "#f9fcff" }}>
        <div className="container">
          <div className="predyx7-header mb-5">
            <h2 className="predyx7-title text-center">
              {parse(cms[88]?.cms_page_heading || "The Evolution of <span>Clinical Understanding</span>")}
            </h2>
          </div>
          <div className="predyx7-card flow">
            <div className="predyx7-trajectory">👂  1816 — Auscultation (Stethoscope)</div>      
            <div className="predyx7-down-arrow">&#8595;</div>
            <div className="predyx7-trajectory">  1895 — Medical Imaging</div>
            <div className="predyx7-down-arrow">&#8595;</div>
            <div className="predyx7-trajectory">❤️  1903 — Cardiac Electrophysiology (ECG)</div>
            <div className="predyx7-down-arrow">&#8595;</div>
            <div className="predyx7-trajectory">🧬  The Genomic Era</div>
            <div className="predyx7-down-arrow">&#8595;</div>
            <div className="predyx7-trajectory">💻  Digital Medicine</div>
            <div className="predyx7-down-arrow">&#8595;</div>
            <div className="predyx7-trajectory">🔷  Clinical Intelligence    Predyx </div>

        
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* NOTE FROM FOUNDER - CMS ID 91 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4 pr-5 pl-6">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[89]?.cms_page_heading || "A Note from <span>the Founder</span>")}
            </h2>
          </div>
          
          <div className="predyx-content-box">
            <div className="info-card" style={{ padding: "0px" }}>
              {parse(cms[89]?.cms_page_description || "")}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER CTA - CMS ID 92 */}
      {/* ============================================ */}
      <section className="funfacts" ref={ref}>
        <div className="overlay">
          <div className="container" style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "28px", color: "#fff", fontWeight: "600", marginBottom: "20px" }}>
              {parse(cms[90]?.cms_page_heading || "Every generation of medicine has been defined by a new way of seeing.")}
            </h2>
            <p style={{ fontSize: "16px", color: "#fff", marginBottom: "30px" }}>
              {parse(cms[90]?.cms_page_small_description || "")}
            </p>
            <a href="#" className="action-items-primary-btn1">
              That is why Predyx exists.
              <i className="fas fa-chevron-right" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;